import { useCallback, useEffect, useMemo, useState } from "react";
import { MerchantApi } from "@/api";
import { buildStorefrontStats } from "./mockData";
import { PublicOfferDetailModal } from "../shared/PublicOfferDetailModal";
import type {
  GetAllOffersResponse,
  PublicOffer,
} from "../shared/publicOffer.types";
import { mapGetAllOffersResponse } from "./storefrontOffer.utils";
import type {
  GetPublicMerchantsOfOfferResponse,
  MerchantOfOfferVO,
  OfferCategoryVO,
} from "./types";
import { StorefrontHero } from "./components/StorefrontHero";
import { StorefrontStats } from "./components/StorefrontStats";
import { StorefrontMerchantsSection } from "./components/StorefrontMerchantsSection";
import { OfferDiscovery } from "./components/OfferDiscovery";
import { StorefrontFooter } from "./components/StorefrontFooter";

const LOYYAL_LOGO_URL = "/offers/loyyal1.png";

const STOREFRONT_OFFERS_PAGE_SIZE = 10;
const STOREFRONT_MERCHANTS_PAGE_SIZE = 10;
/** Page size used to populate the merchant filter dropdown */
const STOREFRONT_FILTER_MERCHANTS_PAGE_SIZE = 500;

const PAGE_BG =
  "linear-gradient(160deg, #f5f3ff 0%, #faf5ff 15%, #f0f9ff 40%, #f8fafc 70%, #ede9fe 100%)";

function scrollToSection(id: string) {
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function OfferStorefront() {
  const [detailOffer, setDetailOffer] = useState<PublicOffer | null>(null);
  const [discoveryOffers, setDiscoveryOffers] = useState<
    ReturnType<typeof mapGetAllOffersResponse>["discoveryOffers"]
  >([]);
  const [merchants, setMerchants] = useState<MerchantOfOfferVO[]>([]);
  const [totalMerchantCount, setTotalMerchantCount] = useState(0);
  const [merchantPageNumber, setMerchantPageNumber] = useState(0);
  const [loadingMerchants, setLoadingMerchants] = useState(true);
  const [loadingDiscovery, setLoadingDiscovery] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchTitle, setSearchTitle] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [totalOfferCount, setTotalOfferCount] = useState(0);
  const [categories, setCategories] = useState<OfferCategoryVO[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null,
  );
  const [filterMerchants, setFilterMerchants] = useState<MerchantOfOfferVO[]>(
    [],
  );
  const [selectedMerchantId, setSelectedMerchantId] = useState<number | null>(
    null,
  );

  const apiBaseUrl = useMemo(
    () => (import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL || "").trim(),
    [],
  );

  const totalPages = Math.max(
    1,
    Math.ceil(totalOfferCount / STOREFRONT_OFFERS_PAGE_SIZE),
  );

  const merchantTotalPages = Math.max(
    1,
    Math.ceil(totalMerchantCount / STOREFRONT_MERCHANTS_PAGE_SIZE),
  );

  const stats = useMemo(
    () => buildStorefrontStats(totalOfferCount, totalMerchantCount),
    [totalOfferCount, totalMerchantCount],
  );

  const handleOpenDetail = useCallback((offer: PublicOffer) => {
    setDetailOffer(offer);
  }, []);

  const handleExploreOffers = useCallback(() => scrollToSection("offers"), []);
  const handleViewMerchants = useCallback(
    () => scrollToSection("merchants"),
    [],
  );

  const handleSearchTitleChange = useCallback((title: string) => {
    setSearchTitle(title);
  }, []);

  const handleCategoryChange = useCallback((categoryId: number | null) => {
    setSelectedCategoryId(categoryId);
    setPageNumber(0);
  }, []);

  const handleMerchantChange = useCallback((merchantId: number | null) => {
    setSelectedMerchantId(merchantId);
    setPageNumber(0);
  }, []);

  const handleMerchantCardSelect = useCallback(
    (merchantId: number) => {
      setFilterMerchants((prev) => {
        if (prev.some((m) => m.id === merchantId)) return prev;
        const fromPage = merchants.find((m) => m.id === merchantId);
        return fromPage ? [...prev, fromPage] : prev;
      });
      setSelectedMerchantId(merchantId);
      setPageNumber(0);
      scrollToSection("offers");
    },
    [merchants],
  );

  useEffect(() => {
    setPageNumber(0);
  }, [searchTitle]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await MerchantApi.getAllCategories();
        if (cancelled) return;

        const rawList = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : [];

        // Unique by categoryCode so the dropdown doesn't list duplicates
        const uniqueByCode = new Map<string, OfferCategoryVO>();
        for (const item of rawList) {
          if (!item || item.id == null) continue;
          const code = String(item.categoryCode || item.categoryName || item.id);
          if (uniqueByCode.has(code)) continue;
          uniqueByCode.set(code, {
            id: Number(item.id),
            categoryCode: String(item.categoryCode || ""),
            categoryName: String(item.categoryName || item.categoryCode || ""),
            cashbackPercentage: item.cashbackPercentage,
            merchantOfOfferId: item.merchantOfOfferId,
          });
        }
        setCategories(Array.from(uniqueByCode.values()));
      } catch {
        if (!cancelled) {
          setCategories([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await MerchantApi.getPublicMerchantsOfOffer({
          merchantId: null,
          programId: null,
          programIds: [],
          country: null,
          merchantName: null,
          pageNumber: 0,
          pageSize: STOREFRONT_FILTER_MERCHANTS_PAGE_SIZE,
          sortBy: null,
          sortOrder: null,
        });
        if (cancelled) return;

        const data = (res?.data || {}) as GetPublicMerchantsOfOfferResponse;
        const merchantList = Array.isArray(data.merchantOfOfferVOList)
          ? data.merchantOfOfferVOList
          : [];
        setFilterMerchants(merchantList);
      } catch {
        if (!cancelled) {
          setFilterMerchants([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoadingMerchants(true);
      try {
        const res = await MerchantApi.getPublicMerchantsOfOffer({
          merchantId: null,
          programId: null,
          programIds: [],
          country: null,
          merchantName: null,
          pageNumber: merchantPageNumber,
          pageSize: STOREFRONT_MERCHANTS_PAGE_SIZE,
          sortBy: null,
          sortOrder: null,
        });
        if (cancelled) return;

        const data = (res?.data || {}) as GetPublicMerchantsOfOfferResponse;
        const merchantList = Array.isArray(data.merchantOfOfferVOList)
          ? data.merchantOfOfferVOList
          : [];
        const totalMerchants = Number(data.totalNumberOfMerchants);

        setMerchants(merchantList);
        setTotalMerchantCount(
          Number.isFinite(totalMerchants) ? totalMerchants : merchantList.length,
        );
      } catch {
        if (!cancelled) {
          setMerchants([]);
          setTotalMerchantCount(0);
        }
      } finally {
        if (!cancelled) {
          setLoadingMerchants(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [merchantPageNumber]);

  useEffect(() => {
    let cancelled = false;
    const isBackgroundFetch =
      searchTitle.length > 0 ||
      pageNumber > 0 ||
      selectedCategoryId != null ||
      selectedMerchantId != null;

    (async () => {
      if (isBackgroundFetch) {
        setLoadingDiscovery(true);
      } else {
        setLoadingDiscovery(true);
        setLoadError(false);
      }

      try {
        const res = await MerchantApi.getAllOffers({
          startDateInMillis: "",
          endDateInMillis: "",
          pageSize: STOREFRONT_OFFERS_PAGE_SIZE,
          pageNumber,
          title: searchTitle,
          categoryId: selectedCategoryId,
          merchantId: selectedMerchantId,
        });
        if (cancelled) return;

        const data = (res?.data || {}) as GetAllOffersResponse;
        const mapped = mapGetAllOffersResponse(data, apiBaseUrl);
        const totalOffers = Number(data.totalNumberOfOffers);
        const resolvedTotal = Number.isFinite(totalOffers) ? totalOffers : 0;

        setTotalOfferCount(resolvedTotal);
        setDiscoveryOffers(mapped.discoveryOffers);
      } catch {
        if (!cancelled) {
          if (isBackgroundFetch) {
            setDiscoveryOffers([]);
          } else {
            setLoadError(true);
            setDiscoveryOffers([]);
            setTotalOfferCount(0);
          }
        }
      } finally {
        if (!cancelled) {
          setLoadingDiscovery(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    searchTitle,
    pageNumber,
    selectedCategoryId,
    selectedMerchantId,
    apiBaseUrl,
  ]);

  return (
    <div
      className="h-screen min-h-0 overflow-x-hidden overflow-y-auto [&_*]:!rounded-none"
      style={{ background: PAGE_BG }}
    >
      <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white">
        <div className="flex h-16 w-full items-center justify-between px-4 sm:px-6 lg:px-8">
          <a
            href="/offers/storefront"
            className="inline-flex shrink-0 items-center"
          >
            <span className="inline-flex items-center bg-white p-1">
              <img
                src={LOYYAL_LOGO_URL}
                alt="Loyyal"
                className="block h-9 w-auto max-w-[140px] bg-white object-contain object-left sm:h-10 sm:max-w-[160px]"
                style={{ backgroundColor: "#ffffff" }}
              />
            </span>
          </a>
          <nav className="flex items-center gap-6" aria-label="Main navigation">
            <button
              type="button"
              onClick={() => scrollToSection("offers")}
              className="text-sm font-medium text-slate-600 transition-colors hover:text-violet-700"
            >
              Offers
            </button>
            <button
              type="button"
              onClick={() => scrollToSection("merchants")}
              className="hidden text-sm font-medium text-slate-600 transition-colors hover:text-violet-700 sm:inline"
            >
              Merchants
            </button>
            <button
              type="button"
              onClick={handleExploreOffers}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition-all hover:scale-[1.02] hover:shadow-lg"
            >
              Get Started
            </button>
          </nav>
        </div>
      </header>

      <main>
        <StorefrontHero
          onExploreOffers={handleExploreOffers}
          onViewMerchants={handleViewMerchants}
        />
        <StorefrontStats stats={stats} />

        {loadError ? (
          <p className="px-4 py-8 text-center text-sm text-slate-600 sm:px-6">
            We couldn&apos;t load offers right now. Please try again later.
          </p>
        ) : null}

        <StorefrontMerchantsSection
          merchants={merchants}
          totalMerchantCount={totalMerchantCount}
          loading={loadingMerchants}
          pageNumber={merchantPageNumber}
          totalPages={merchantTotalPages}
          onPageChange={setMerchantPageNumber}
          onMerchantSelect={handleMerchantCardSelect}
        />

        <OfferDiscovery
          offers={discoveryOffers}
          loading={loadingDiscovery}
          totalOfferCount={totalOfferCount}
          pageNumber={pageNumber}
          totalPages={totalPages}
          categories={categories}
          selectedCategoryId={selectedCategoryId}
          onCategoryChange={handleCategoryChange}
          merchants={filterMerchants}
          selectedMerchantId={selectedMerchantId}
          onMerchantChange={handleMerchantChange}
          onPageChange={setPageNumber}
          onOpenDetail={handleOpenDetail}
          onSearchTitleChange={handleSearchTitleChange}
        />
      </main>

      <StorefrontFooter />

      <PublicOfferDetailModal
        offer={detailOffer}
        onOpenChange={(open) => !open && setDetailOffer(null)}
      />
    </div>
  );
}
